import React, { useEffect, useState } from 'react';
import { CellCommercialConfig } from "@m4a3/api-client/dist/models/cell-commercial-config.interface";
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

interface CellCommercialConfigEditorProps {
  isLoading: boolean;
  cellCommercialConfigLoadingError: Error;
  cellCommercialConfig: CellCommercialConfig;
  onChangeDeliveryFlightTime: (newVal: number) => Promise<void>;
  onChangeOpsBufferTime: (newVal: number) => Promise<void>;
}

const CellCommercialConfigEditor = (props: CellCommercialConfigEditorProps) => {
  const [isEditingDroneDeliveryTime, setIsEditingDroneDeliveryTime] = useState(false);
  const [isEditingOpsBufferTime, setIsEditingOpsBufferTime] = useState(false);
  const [fieldValues, setFieldValues] = useState({
    droneFlightTimeInMinutes: props.cellCommercialConfig?.droneFlightTimeInMinutes,
    opsBufferTimeInMinutes: props.cellCommercialConfig?.opsBufferTimeInMinutes,
  });

  useEffect(() => {
    setFieldValues({
      droneFlightTimeInMinutes: props.cellCommercialConfig?.droneFlightTimeInMinutes,
      opsBufferTimeInMinutes: props.cellCommercialConfig?.opsBufferTimeInMinutes,
    });
  }, [props.cellCommercialConfig])

  const handleChange = (field: string, value: number) => {
    setFieldValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (field: string) => {
    if (field === 'droneFlightTimeInMinutes') {
      props.onChangeDeliveryFlightTime(fieldValues.droneFlightTimeInMinutes);
      setIsEditingDroneDeliveryTime(false);
    } else {
      props.onChangeOpsBufferTime(fieldValues.opsBufferTimeInMinutes);
      setIsEditingOpsBufferTime(false);
    }
  };

  const DropdownSelector = ({ field }: { field: 'droneFlightTimeInMinutes' | 'opsBufferTimeInMinutes' }) => {
    const numberOfOptions = 10;
    const incrementBy = 1
    const options = Array.from({ length: numberOfOptions }, (_, i) => i + incrementBy);

    return (<select
      name={`${field}-selector`}
      value={fieldValues[field]}
      onChange={(e) => handleChange(field, parseInt(e.target.value))}
      className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6">
      {options.map((value) => (
        <option key={value} value={value}>
          {value} minute{value > 1 ? 's' : ''}
        </option>
      ))}
    </select>);
  }

  const pluralizeMinutes = (value: number) => `${value} minute${value > 1 ? 's' : ''}`;

  const EditButtons = ({ field }: { field: 'droneFlightTimeInMinutes' | 'opsBufferTimeInMinutes' }) => (
    <div className='mt-2'>
      <button
        type="button"
        onClick={() => field === 'droneFlightTimeInMinutes' ? setIsEditingDroneDeliveryTime(false) : setIsEditingOpsBufferTime(false)}
        className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-2"
      >
        Cancel
      </button>
      <button
        onClick={() => handleSave(field)}
        type="button"
        className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        Save
      </button>
    </div>
  );

  const renderField = (field: 'droneFlightTimeInMinutes' | 'opsBufferTimeInMinutes') => {
    const isEditing = field === 'droneFlightTimeInMinutes' && isEditingDroneDeliveryTime || field === 'opsBufferTimeInMinutes' && isEditingOpsBufferTime;

    return (<div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
      <dt className="truncate text-sm font-medium text-gray-500">{field === 'droneFlightTimeInMinutes' ? 'Delivery flight time (one-way)' : 'Ops buffer time'}</dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
        {isEditing ?
          <DropdownSelector field={field} /> : pluralizeMinutes(fieldValues[field])}
      </dd>
      <div className="flex flex-row-reverse">
        {isEditing ?
          <EditButtons field={field} /> : (
          <button onClick={() => field === 'droneFlightTimeInMinutes' ? setIsEditingDroneDeliveryTime(true) : setIsEditingOpsBufferTime(true)}
            type="button" className="text-sm font-semibold leading-6 text-blue-600">
            Edit
          </button>
        )}
      </div>
    </div>);
  };

  if (props.cellCommercialConfigLoadingError) {
    return <ErrorCard
      title='Error loading cell commercial config.'
      message={`The error has been logged.\nError message: ${props.cellCommercialConfigLoadingError.message}`}
      callToActionText='Refresh the page'
      onClickCallToAction={() => {
        window.location.reload();
      }}
      />
  }

  return (
    <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
      {(props.isLoading) ? <div className='sm:col-span-2 flex justify-center items-center'>
        <div className='flex items-center flex-col'>
          <LoadingSpinner />
          <p>Loading...</p>
        </div>
      </div> : <>
        {renderField('droneFlightTimeInMinutes')}
        {renderField('opsBufferTimeInMinutes')}
      </>}
    </dl>
  );
}

export default CellCommercialConfigEditor;
